import { useState } from 'react';
import { Entity, getCompoundEntityRef } from '@backstage/catalog-model';
import {
  InfoCard,
  Link,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { CheckResult } from '@backstage-community/plugin-tech-insights-common';
import {
  TechInsightsApi,
  techInsightsApiRef,
} from '@backstage-community/plugin-tech-insights-react';
import {
  Box,
  Button,
  Collapse,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@material-ui/core';
import PassIcon from '@material-ui/icons/CheckCircleOutline';
import FailIcon from '@material-ui/icons/HighlightOff';
import UndeterminedIcon from '@material-ui/icons/ReportProblemOutlined';
import useAsync from 'react-use/lib/useAsync';
import { isUndetermined } from '../../lib/techInsightsCheckResultRenderer';
import { FailureReason, failureReason } from './failureReasons';

type Status = 'failing' | 'undetermined' | 'passing';

const STATUS_ICON = {
  failing: <FailIcon color="error" />,
  undetermined: <UndeterminedIcon color="disabled" />,
  passing: <PassIcon style={{ color: '#2e7d32' }} />,
};

const EMPTY_MESSAGE =
  'No checks have results for this entity yet. Checks run against Components that have a github.com/project-slug annotation. Catalog facts refresh hourly and repository facts daily, so a newly added component fills in after the next run.';

const statusOf = (api: TechInsightsApi, result: CheckResult): Status => {
  if (isUndetermined(result)) return 'undetermined';
  return api.isCheckResultFailed(result) ? 'failing' : 'passing';
};

export const groupByStatus = (api: TechInsightsApi, results: CheckResult[]) => {
  const grouped: Record<Status, CheckResult[]> = {
    failing: [],
    undetermined: [],
    passing: [],
  };
  results.forEach(result => grouped[statusOf(api, result)].push(result));
  return grouped;
};

/** Why a check failed, with a link to the file or entry to change. */
const Explanation = ({ reason, where }: FailureReason) => (
  <>
    {reason}
    {where && (
      <>
        {' '}
        <Link to={where.url} onClick={e => e.stopPropagation()}>
          {where.title} ↗
        </Link>
      </>
    )}
  </>
);

/** One check: its name and fix links, with the full description on click. */
const CheckRow = ({
  result,
  status,
  entity,
}: {
  result: CheckResult;
  status: Status;
  entity: Entity;
}) => {
  const api = useApi(techInsightsApiRef);
  const [open, setOpen] = useState(false);
  const [renderer] = api.getCheckResultRenderers([result.check.type]);
  const links =
    status === 'passing'
      ? []
      : api.getLinksForEntity(result, entity, { includeStaticLinks: true });
  const explanation =
    status === 'failing' ? failureReason(result, entity) : undefined;
  return (
    <>
      <ListItem dense button onClick={() => setOpen(!open)}>
        <ListItemIcon>{STATUS_ICON[status]}</ListItemIcon>
        <ListItemText
          primary={result.check.name}
          secondary={explanation && <Explanation {...explanation} />}
        />
        {links[0] && (
          <Box ml={1} flexShrink={0} onClick={e => e.stopPropagation()}>
            <Link to={links[0].url} title={links[0].title}>
              How to fix →
            </Link>
          </Box>
        )}
      </ListItem>
      <Collapse in={open}>
        <Box pl={9} pr={2} pb={1}>
          {renderer?.description?.(result) ?? result.check.description}
        </Box>
      </Collapse>
    </>
  );
};

const ScoreSummary = ({ counts }: { counts: Record<Status, number> }) => {
  const determined = counts.passing + counts.failing;
  return (
    <Box mb={2}>
      <Box display="flex" justifyContent="space-between" mb={1}>
        <Typography variant="h6">
          {counts.passing} of {determined} passing
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {counts.failing} to fix
          {counts.undetermined > 0 &&
            ` · ${counts.undetermined} couldn't check`}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={determined ? (counts.passing / determined) * 100 : 0}
      />
    </Box>
  );
};

/**
 * Replaces upstream's EntityTechInsightsScorecardCard. Scores only the checks
 * that could be evaluated, lists what needs fixing first with links to fix it,
 * and folds the passing checks away.
 */
export const EntityScorecardCard = () => {
  const { entity } = useEntity();
  const api = useApi(techInsightsApiRef);
  const [showPassing, setShowPassing] = useState(false);
  const { value, loading, error } = useAsync(
    () => api.runChecks(getCompoundEntityRef(entity)),
    [api, entity],
  );

  if (loading) return <Progress />;
  if (error) return <ResponseErrorPanel error={error} />;

  const grouped = groupByStatus(api, value ?? []);
  const counts = {
    failing: grouped.failing.length,
    undetermined: grouped.undetermined.length,
    passing: grouped.passing.length,
  };
  const rows = (status: Status) =>
    grouped[status].map(result => (
      <CheckRow
        key={result.check.id}
        result={result}
        status={status}
        entity={entity}
      />
    ));

  return (
    <InfoCard
      title="Scorecard"
      subheader="Engineering standards for this entity"
    >
      {value?.length ? (
        <>
          <ScoreSummary counts={counts} />
          <List disablePadding>
            {rows('failing')}
            {rows('undetermined')}
          </List>
          {counts.passing > 0 && (
            <>
              <Button size="small" onClick={() => setShowPassing(!showPassing)}>
                {showPassing ? '▾' : '▸'} Passing ({counts.passing})
              </Button>
              <Collapse in={showPassing}>
                <List disablePadding>{rows('passing')}</List>
              </Collapse>
            </>
          )}
        </>
      ) : (
        <Typography color="textSecondary">{EMPTY_MESSAGE}</Typography>
      )}
    </InfoCard>
  );
};
